import { DOCUMENT, Injectable, inject } from '@angular/core';

/** Lo que se abre como `<dialog>` modal, de abajo hacia arriba. Los avisos no son una capa: van dentro de la de arriba. */
export type ModalLayer = 'modal' | 'loading';

const RANK: Record<ModalLayer, number> = { modal: 0, loading: 1 };

interface OpenLayer {
  dialog: HTMLDialogElement;
  layer: ModalLayer;
}

/**
 * Orden de lo que tapa la página: 1 página, 2 fondo del modal, 3 modal, 4 carga, 5 avisos.
 *
 * El top layer no mira el z-index: apila por orden de apertura, así que un modal abierto durante una carga quedaría
 * encima del overlay. Y un `<dialog>` modal deja inerte todo lo que no está adentro suyo, aunque se pinte encima
 * (whatwg/html#9936, sin resolver): un aviso afuera se vería, pero no se podría cerrar ni lo leería el lector de pantalla.
 * Por eso Modal y LoadingOverlay avisan acá cuando abren: si ya había abierta una capa más alta, se vuelve a abrir
 * encima; y los avisos se mudan adentro de la capa de más arriba y vuelven a su lugar cuando no queda ninguna.
 */
@Injectable({ providedIn: 'root' })
export class TopLayer {
  private readonly document = inject(DOCUMENT);
  /** De abajo hacia arriba, en el mismo orden que el top layer. */
  private readonly stack: OpenLayer[] = [];
  private notifications: HTMLElement | null = null;
  /** Dónde vivían los avisos antes de mudarse a un diálogo. */
  private notificationsHome: ParentNode | null = null;
  // Cualquier cierre (close(), Esc, el fallback) llega acá en una microtarea, antes del próximo render. El evento
  // `close` llega en una tarea aparte: con él se podría pintar un cuadro con los avisos dentro de un diálogo ya oculto.
  private readonly observer = new MutationObserver(() => this.dropClosed());

  /** Se llama recién abierto el diálogo (después de `showModal()`, o de su fallback): así el foco inicial ya es suyo. */
  opened(dialog: HTMLDialogElement, layer: ModalLayer): void {
    if (this.stack.some((open) => open.dialog === dialog)) {
      return;
    }
    this.observer.observe(dialog, { attributeFilter: ['open'] });
    this.stack.push({ dialog, layer });
    this.raiseAbove(layer);
    this.placeNotifications();
  }

  /** Un diálogo que sale del DOM abierto (se destruyó su página): el navegador ya lo sacó del top layer. */
  released(dialog: HTMLDialogElement): void {
    this.remove(dialog);
    this.placeNotifications();
  }

  /** Se mira el atributo: la pila se entera de un cierre recién en la microtarea del observer. */
  hasOpenModal(): boolean {
    return this.stack.some(({ dialog, layer }) => layer === 'modal' && dialog.hasAttribute('open'));
  }

  /** Los avisos siguen siempre a la capa de más arriba. */
  pinNotifications(host: HTMLElement): void {
    this.notifications = host;
    this.placeNotifications();
  }

  /** Lo que es más alto que `layer` y se abrió antes queda debajo en el top layer: se vuelve a abrir encima. */
  private raiseAbove(layer: ModalLayer): void {
    const higher = this.stack.filter((open) => RANK[open.layer] > RANK[layer]);
    for (const open of higher) {
      this.stack.splice(this.stack.indexOf(open), 1);
      this.stack.push(open);
      this.reopen(open.dialog);
    }
  }

  // Sin showModal no hay top layer: el fallback ya queda encima por z-index.
  private reopen(dialog: HTMLDialogElement): void {
    if (typeof dialog.showModal !== 'function') {
      return;
    }
    // Afuera mientras se reabre: el foco inicial del diálogo no tiene que caer en el cerrar de un aviso.
    if (this.notifications && dialog.contains(this.notifications)) {
      this.goHome(this.notifications);
    }
    // close() devuelve el foco a lo que estaba enfocado cuando el diálogo se abrió, que ahora queda inerte debajo del
    // modal nuevo. Se recupera antes de reabrir: así, al cerrarse de verdad, el foco vuelve adentro del modal nuevo.
    const active = this.document.activeElement;
    dialog.close();
    if (active instanceof HTMLElement) {
      active.focus();
    }
    dialog.showModal();
  }

  // Se mira el estado actual y no los registros: reabrir quita y vuelve a poner `open` en la misma tarea.
  private dropClosed(): void {
    const closed = this.stack.filter((open) => !open.dialog.hasAttribute('open'));
    if (closed.length === 0) {
      return;
    }
    closed.forEach((open) => this.remove(open.dialog));
    this.placeNotifications();
  }

  private remove(dialog: HTMLDialogElement): void {
    const index = this.stack.findIndex((open) => open.dialog === dialog);
    if (index !== -1) {
      this.stack.splice(index, 1);
    }
  }

  private placeNotifications(): void {
    const notifications = this.notifications;
    if (!notifications) {
      return;
    }
    const top = this.stack.at(-1)?.dialog;
    if (!top) {
      this.goHome(notifications);
      return;
    }
    if (notifications.parentNode !== top) {
      this.notificationsHome ??= notifications.parentNode;
      top.append(notifications);
    }
  }

  private goHome(notifications: HTMLElement): void {
    if (!this.notificationsHome) {
      return;
    }
    this.notificationsHome.append(notifications);
    this.notificationsHome = null;
  }
}
